import { useMemo } from "react"
import { CountryCode, CountryCodes } from "./CountryCode"
import { IntlShape, useIntl } from "react-intl"
import {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError
} from "libphonenumber-js"

export const usePhoneCodeSelectOptions = () => {
  const intl = useIntl()
  return useMemo(() => {
    const localeComparator = new Intl.Collator(intl.locale).compare

    const mappedCountryCodes = CountryCodes.map((countryCode) => {
      const formattedCountryCode = intl.formatDisplayName(countryCode, { type: "region" })
      const countryCallingCode = getCountryCallingCode(countryCode)
      return {
        value: countryCode,
        label: intl.formatMessage(
          { defaultMessage: "{countryCode} (+{phoneCode})" },
          { countryCode: formattedCountryCode, phoneCode: countryCallingCode }
        )
      }
    })
    return addCountryCodeEdgeCases(mappedCountryCodes, intl).sort((a, b) => localeComparator(a.label, b.label))
  }, [intl])
}

// WQ-2818: This code is a workaround until our phone library adds AQ & UM
// WQ-2897: This handles the edge case of an unknown country (countryCode "XX") by setting to an empty string (but not accepting this on submit)
function addCountryCodeEdgeCases(countryCodes: { value: CountryCode; label: any }[], intl: IntlShape) {
  countryCodes.push({
    value: "AQ" as CountryCode,
    label: intl.formatMessage(
      { defaultMessage: "{countryCode} (+{phoneCode})" },
      { countryCode: "Antarctica", phoneCode: "672" }
    )
  })
  countryCodes.push({
    value: "UM" as CountryCode,
    label: intl.formatMessage(
      { defaultMessage: "{countryCode} (+{phoneCode})" },
      { countryCode: "U.S. Outlying Islands", phoneCode: "1" }
    )
  })
  countryCodes.push({
    value: "XX" as CountryCode,
    label: ""
  })
  return countryCodes
}

// WQ-2818: This code is a workaround until our phone library adds AQ & UM
export function addCountryOptionsEdgeCases(countryOptions: { label: string; value: string }[], intl: IntlShape) {
  // `of` answers undefined only under `fallback: "none"`; the default falls back to the code itself.
  const aqLabel = new Intl.DisplayNames([intl.locale], { type: "region" }).of("AQ") as string
  countryOptions.push({
    value: "AQ" as CountryCode,
    label: aqLabel
  })
  const umLabel = new Intl.DisplayNames([intl.locale], { type: "region" }).of("UM") as string
  countryOptions.push({
    value: "UM" as CountryCode,
    label: umLabel
  })
  return countryOptions
}

// WQ-2818: This code is a workaround until our phone library adds AQ & UM
// WQ-2897: This handles the edge case of an unknown country (countryCode "XX") by leaving calling code blank
export function getCountryCallingCodeConsideringEdgeCases(countryCode: CountryCode) {
  let countryCallingCode
  if (countryCode === ("AQ" as CountryCode)) {
    countryCallingCode = "672"
  } else if (countryCode === ("UM" as CountryCode)) {
    countryCallingCode = "1"
  } else if (countryCode === ("XX" as CountryCode)) {
    countryCallingCode = null
  } else {
    countryCallingCode = getCountryCallingCode(countryCode)
  }
  return countryCallingCode
}

// WQ-2818: This code is a workaround until our phone library adds AQ & UM
// This is currently only used in our "booking experience"
// The logic is intentionally simple for these edge cases & based on Twilio validation from phone number collection
export function isValidPhoneNumberConsideringEdgeCases(phoneNumber: string, countryCode: CountryCode) {
  if (countryCode === ("AQ" as CountryCode)) {
    const phoneNumberCleaned = phoneNumber.replace(/[^a-zA-Z0-9]/g, "")
    return !(isNaN(Number(phoneNumberCleaned)) || phoneNumber.length !== 6)
  } else if (countryCode === ("UM" as CountryCode)) {
    const phoneNumberCleaned = phoneNumber.replace(/[^a-zA-Z0-9]/g, "")
    return !(isNaN(Number(phoneNumberCleaned)) || phoneNumber.length !== 10)
  } else {
    return isValidPhoneNumber(phoneNumber, countryCode)
  }
}

// `isValidPhoneNumber` alone passes a number typed with another calling code's "+" prefix. The
// picked country only says how to read a national number: any valid number in its calling code
// passes, and `toE164Phone` sends the number's own country (Puerto Rico or Canada under +1).
export function isValidPhoneNumberInCountry(phoneNumber: string, countryCode: CountryCode): boolean {
  // `extract: false` as `isValidPhoneNumber` sets it, so a number inside other text is refused.
  const parsed = parsePhoneNumberFromString(phoneNumber, { defaultCountry: countryCode, extract: false })
  return (
    parsed?.country !== undefined &&
    parsed.isValid() &&
    parsed.countryCallingCode === getCountryCallingCode(countryCode)
  )
}

// Callers pass only numbers `isValidPhoneNumberInCountry` accepted for `countryCode`: anything else
// can throw. The country sent is the number's own, which the BFF checks the number against.
export function toE164Phone(
  phoneNumber: string,
  countryCode: CountryCode
): { phoneNumber: string; phoneCountryCode: string } {
  const parsed = parsePhoneNumberWithError(phoneNumber, countryCode)
  return { phoneNumber: parsed.number, phoneCountryCode: parsed.country ?? countryCode }
}
